import SearchIcon from '@heroicons/react/outline/SearchIcon';
import classNames from 'classnames';
import { useField } from 'formik';
import {
  forwardRef,
  HTMLProps,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useBreakpoint } from '~/hooks/useBreakpoint';

const IDENTITY = { nextVal: 1 };

export type Props = {
  label: string;
  flow?: 'condensed';
  align?: 'right';
  error?: string | false;
  required?: boolean;
  placeholder?: string;
  filter?: boolean;
  filtered?: boolean;
  /**
   * Select the input text/value when input is focused
   */
  selectOnFocus?: boolean;
  icon?: ReactNode;
  isSearchField?: boolean;
  growInput?: boolean;
  hint?: string;
} & HTMLProps<HTMLInputElement>;
type Ref = HTMLInputElement;

export const Input = forwardRef<Ref, Props>(
  (
    {
      label,
      align,
      error,
      placeholder,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      selectOnFocus = false,
      icon,
      filter,
      filtered,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      isSearchField,
      type = 'text',
      growInput = true,
      hint,
      ...props
    },
    ref
  ) => {
    const uniqueId = useMemo(
      () => props.id ?? `Input-${IDENTITY.nextVal++}`,
      [props.id]
    );
    const { isMobile } = useBreakpoint();
    const [isAnimating, setIsAnimating] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Apply animation when error appears
    useEffect(() => {
      if (error && !isAnimating) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Input error detected, starting animation');
        }
        setIsAnimating(true);

        // Clear animation after 1820ms (same as CreateJobForm)
        timeoutRef.current = setTimeout(() => {
          if (process.env.NODE_ENV === 'development') {
            console.log('Input animation timeout, clearing animation');
          }
          setIsAnimating(false);
        }, 1820);
      }

      // Cleanup function
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    }, [error, isAnimating]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <div
        className={classNames(
          {
            'mb-5 flex flex-col gap-1': props.flow !== 'condensed' && !filter,
            'min-w-[140px] flex-1': type === 'date',
          },
          'relative',
          { error: !!error },
          props.className,
          isAnimating && 'animate-shake'
        )}
        {...props}
        data-field=''
      >
        {label && (
          <label
            className={classNames(
              { 'block pr-3': props.flow === 'condensed' },
              { 'hidden pb-1 lg:block': type === 'search' },
              filter ? 'invisible absolute' : 'ml-1 text-sm',
              'font-medium text-grey-50',
              'overflow-hidden whitespace-nowrap text-wrap text-left'
            )}
            htmlFor={uniqueId}
          >
            {label}
            {props.required && <span className='pl-0.5 text-brand'>*</span>}
          </label>
        )}
        <div
          className={classNames('relative flex', {
            'flex-1': growInput,
          })}
        >
          {type === 'search' && (
            <div className='absolute bottom-0 left-3 top-0 flex items-center lg:bottom-5 lg:left-3'>
              <SearchIcon className='mt-0.5 h-5 w-5 text-grey-20 lg:h-6 lg:w-6' />
            </div>
          )}
          <input
            ref={ref}
            type={type}
            {...props}
            id={uniqueId}
            placeholder={placeholder ?? ''}
            style={{ fontSize: 16 }}
            className={classNames(
              // Base styles for all inputs
              'input scroll-mt-8 border bg-white px-3 focus-visible:outline-none',
              // Input type variants
              {
                // Search variant
                'lg:border-1 w-full rounded-full border-none bg-white bg-opacity-20 pl-10 pr-3 text-white placeholder:text-white lg:mb-5 lg:rounded lg:border-solid lg:border-outline-variant lg:text-primary lg:placeholder:text-tertiary':
                  type === 'search',
                // Filter variant
                'w-full cursor-pointer whitespace-nowrap rounded-full border-grey-10 py-[3px] text-sm leading-tight placeholder:text-center placeholder:text-grey-50 lg:py-1 lg:text-base':
                  filter,
                // Default variant with error state
                'mt-0.5 w-full rounded border-red-600 py-2.5 focus-visible:border-red-600':
                  !filter && error,
                // Default variant
                'focus-visible:border-outline mt-0.5 w-full rounded border-outline-variant py-2.5':
                  !filter && !error,
              },

              // Filtered state modifier
              {
                'border-outline-variant bg-grey-5 text-center font-semibold text-grey-50':
                  filtered,
              },

              // Layout modifiers
              {
                'text-right': align === 'right',
                'py-2': props.flow === 'condensed',
                'border-transparent bg-transparent py-0':
                  props.disabled && props.flow === 'condensed',
              },

              // Date input specific styles
              {
                'h-[46px] min-w-0 flex-1 appearance-none whitespace-nowrap py-2.5 pl-3 pr-2 xs:pr-3':
                  type === 'date',
                'flex-1 md:flex-initial': isMobile && type === 'date',
              },
              // Icon modifier
              {
                'pr-[45px]': icon,
              }
            )}
          />
          <div
            className='absolute right-2 top-0 h-full text-gray-400'
            aria-hidden='true'
          >
            <div className='flex h-full items-center'>{icon}</div>
          </div>
        </div>
        {error && (
          <div className='ErrorMessage text-left'>
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </div>
        )}
        {hint && !error && (
          <div className='text-left text-xs text-gray-600'>{hint}</div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

type TextFieldProps = Props & {
  /** @todo figure out how to type this correctly, or have extend the forwardRef props instead of Props */
  ref?: any;
  name: string;
  label: string;
  align?: 'right';
};

export const TextField = ({ name = '', ...props }: TextFieldProps) => {
  const [field, meta] = useField<string>(name);

  return <Input {...field} {...props} error={meta.touched && meta.error} />;
};

TextField.displayName = 'TextField';
